import { useEffect } from "react";
import { Bell, Search, Settings, Sliders } from "lucide-react";
import type { ShellModal } from "./AppShell.js";
import { ThemeToggle } from "./ThemeToggle.js";
import { useAlerts } from "../lib/alerts.js";
import { cn } from "../lib/cn.js";

interface Props {
  customizing: boolean;
  onCustomize: () => void;
  onOpen: (modal: ShellModal) => void;
}

/**
 * The design's right-hand icon rail: customise, theme, search, notifications,
 * settings.
 *
 * Every one of them is a real `<button>` with a real accessible name. The
 * design gave these `title` attributes only, which is not reliably surfaced as
 * an accessible name and is not shown at all on touch -- five unnamed buttons
 * in a row is a straight axe failure and an unusable header for anyone reading
 * by name.
 */
export function HeaderActions({ customizing, onCustomize, onOpen }: Props) {
  const alerts = useAlerts();

  // The design advertises Ctrl+K in a tooltip and never binds it.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "k" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      onOpen("search");
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onOpen]);

  return (
    <>
      <button
        type="button"
        onClick={onCustomize}
        aria-expanded={customizing}
        className={cn(
          "flex items-center gap-1.5 rounded-full border p-2 text-sm transition-colors sm:p-2.5",
          customizing
            ? "border-btn bg-btn font-semibold text-btn-ink"
            : "border-line bg-line/40 text-ink-muted hover:bg-line hover:text-ink",
        )}
      >
        <Sliders size={18} aria-hidden="true" />
        <span className="text-xs font-medium max-xl:sr-only">
          {customizing ? "Done" : "Customize"}
        </span>
      </button>

      <ThemeToggle />

      <IconButton
        label="Search"
        onClick={() => {
          onOpen("search");
        }}
      >
        <Search size={18} aria-hidden="true" />
      </IconButton>

      <IconButton
        label={
          alerts.length === 0
            ? "Notifications, nothing to report"
            : `Notifications, ${String(alerts.length)} to read`
        }
        onClick={() => {
          onOpen("notifications");
        }}
        className="max-sm:hidden"
      >
        <Bell size={18} aria-hidden="true" />
        {alerts.length > 0 && (
          // Not the only carrier: the button's own name says how many, so a
          // reader who cannot see the dot is not told less.
          <span
            aria-hidden="true"
            className="absolute right-1 top-1 h-2 w-2 rounded-full bg-accent ring-2 ring-page"
          />
        )}
      </IconButton>

      <IconButton
        label="Settings"
        onClick={() => {
          onOpen("settings");
        }}
        className="max-sm:hidden"
      >
        <Settings size={18} aria-hidden="true" />
      </IconButton>
    </>
  );
}

function IconButton({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative rounded-full border border-line bg-line/40 p-2 text-ink-muted transition-colors hover:bg-line hover:text-ink sm:p-2.5",
        className,
      )}
    >
      <span className="sr-only">{label}</span>
      {children}
    </button>
  );
}
